import * as Sentry from '@sentry/vue';
import { defineStore } from 'pinia';

import { useRtmStore } from '@/stores/RtmStore';
import { getRtmUsers } from '@/flagler-api/users'; // required to populate rtm users

import { Role, Permission, User } from '@/types/Api';
import { UserData } from '@/types/Ui';

import { usePreferencesStore } from './PreferencesStore';

export const useUserStore = defineStore('UserStore', () => {
  // all available roles
  const roles = ref<Role[] | undefined>();
  // all available permissions
  const permissions = ref<Permission[] | undefined>();
  // logged-in user data
  const userData = ref<UserData | undefined>();
  const rtmStore = useRtmStore();
  const { users: rtmUsers } = storeToRefs(rtmStore);
  const preferencesStore = usePreferencesStore();
  const { coveringForUserIds, coveringForUserIdsSetDate } =
    storeToRefs(preferencesStore);

  const roleOptions = computed(
    (): { title: string; value: string; privileged: boolean }[] => {
      if (roles.value !== undefined) {
        return roles.value
          .map((r) => ({
            title: r.prettyName ?? r.name,
            value: r.name,
            privileged: !!r.privileged,
          }))
          .sort((a, b) => a.title.localeCompare(b.title));
      } else {
        return [];
      }
    }
  );

  const permissionOptions = computed(
    (): { title: string; value: Permission }[] => {
      if (permissions.value !== undefined) {
        return permissions.value.map((p) => {
          return { title: `${p.action} ${p.subject}`, value: p };
        });
      } else {
        return [];
      }
    }
  );

  watch(userData, (u: UserData | undefined) => {
    if (u) {
      localStorage.setItem('userData', JSON.stringify(u));
      Sentry.setUser({
        email: u.email,
        id: u._id,
        username: u.fullName,
      });
    } else {
      Sentry.setUser(null);
    }
  });

  const keyedRoles = computed((): { [key: string]: Role } => {
    const result: { [key: string]: Role } = {};
    if (userData.value && userData.value.roles && roles.value) {
      for (const roleName of userData.value.roles) {
        const role = roles.value?.find((r) => r.name === roleName);
        if (role) {
          result[roleName] = role;
        }
      }
    }
    return result;
  });

  const coveringForUsers = computed((): Users[] => {
    return rtmUsers.value.filter((u) =>
      coveringForUserIds.value.includes(u._id)
    );
  });

  const allUsers = computed(() => {
    if (userData.value) {
      return [userData.value, ...coveringForUsers.value];
    } else {
      return coveringForUsers.value;
    }
  });

  const allUserIds = computed(() => {
    if (userData.value) {
      return [userData.value._id, ...coveringForUserIds.value];
    } else {
      return coveringForUserIds.value;
    }
  });

  const myUserId = computed(
    (): string | undefined => userData.value?._id || undefined
  );

  // adding and removing users refers to users the logged-in user is covering for; the additional
  // users' notifications and events are shown along with the logged-in user
  // requires that getRtmUsers() has been called
  const addUser = (user: User) => {
    addUserId(user._id);
  };

  const addUserId = (userId: string) => {
    coveringForUserIds.value.push(userId);
  };

  const removeUserId = (userId: string) => {
    coveringForUserIds.value = coveringForUserIds.value.filter(
      (id) => id !== userId
    );
  };

  const replaceUserIds = (userIds: string[]) => {
    coveringForUserIds.value = userIds;
  };

  watch(coveringForUsers, () => {
    coveringForUserIdsSetDate.value = Date.now();
  });

  // intended to be called with results of user/startup
  const startup = (rolesAndPermissions?: {
    roles: Role[];
    permissions: Permission[];
  }) => {
    if (rolesAndPermissions) {
      roles.value = rolesAndPermissions.roles;
      permissions.value = rolesAndPermissions.permissions;
    }
    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
      userData.value = JSON.parse(userDataString);
    }
    getRtmUsers(); // @TODO rtmStore should be responsible for populating itself once after login
    if (coveringForUserIdsSetDate.value !== undefined) {
      const today = new Date();
      const lastSet = new Date(coveringForUserIdsSetDate.value);
      // if the calendar day has changed, forget who we're covering for
      if (today.toDateString() !== lastSet.toDateString()) {
        coveringForUserIds.value = [];
        coveringForUserIdsSetDate.value = undefined;
      }
    }
  };

  // does logged in user have a given role
  const hasRole = (roleName: string) => {
    return keyedRoles.value[roleName] !== undefined;
  };

  return {
    // getters
    roles,
    roleOptions,
    permissions,
    permissionOptions,
    userData,
    keyedRoles,
    coveringForUsers,
    allUsers,
    allUserIds,
    myUserId,

    // actions
    hasRole,
    addUser,
    addUserId,
    removeUserId,
    replaceUserIds,
    startup,
  };
});
